/**
 * Builds a context object for the AI chat assistant based on the current
 * project/workflow state. The context is sent to the backend and used to
 * construct a state-aware system prompt for GPT-4o.
 *
 * live = useWorkflowLive() result:
 *   live.status.status  → UPPERCASE: 'RUNNING'|'COMPLETED'|'FAILED'|'PAUSED'
 *   live.status.done    → boolean
 *   live.currentStep    → computed current step name
 *   live.snapshot       → full snapshot: { pending_human_request, partials, run_meta, ... }
 *
 * Metrics priority:
 *   1. projectMeta.last_model.metrics  (persisted after workflow finishes)
 *   2. live.snapshot.partials.metrics  (flat dict at root of partials)
 *   3. live.snapshot.partials.designer.models[0].metrics  (array of {metric_name, metric_value})
 *
 * @param {object} live        - useWorkflowLive() result
 * @param {object} project     - { id, name }
 * @param {string} activeTab   - 'flow'|'details'|'artifacts'|'monitoring'|'inference'
 * @param {Array}  tables      - [{ name, columns: [{ name, type }] }]
 * @param {object} projectMeta    - persisted project meta: { last_model, last_workflow_id, ... }
 * @param {object} monitoringData - { metrics, drift } from monitoring API (only on monitoring tab)
 * @returns {object} context
 */
export function buildChatContext(live, project, activeTab, tables, projectMeta, monitoringData) {
  const snapshot = live?.snapshot
  const liveStatus = live?.status          // raw status object from API
  const statusStr = liveStatus?.status     // uppercase: 'RUNNING'|'COMPLETED'|'FAILED'|'PAUSED'|undefined
  const isDone = !!liveStatus?.done
  const partials = snapshot?.partials || {}
  const pendingHR = snapshot?.pending_human_request

  const base = {
    workflowId: snapshot?.run_meta?.workflow_id || null,
    tables: (tables || []).map(t => ({
      name: t.name || t.dataset_path?.split('/').pop() || 'table',
      columns: (t.columns || []).map(c => ({ name: c.name, type: c.type })),
    })),
  }

  // ── Human review / PAUSED (highest priority) ─────────────────────────────
  if (pendingHR || statusStr === 'PAUSED') {
    return {
      ...base,
      state: 'human_review',
      currentStep: live?.currentStep || pendingHR?.agent || 'agent',
      reviewMessage: pendingHR?.request?.message || '',
      reviewFields: pendingHR?.request?.fields_needed || [],
      demandQuestions: partials?.demand?.questions || [],
    }
  }

  // ── Workflow running ──────────────────────────────────────────────────────
  if (statusStr === 'RUNNING' || (!isDone && statusStr && statusStr !== 'COMPLETED' && statusStr !== 'FAILED')) {
    const startedAt = snapshot?.run_meta?.started_at
    let elapsed = null
    if (startedAt) {
      const raw = startedAt
      const started = new Date(raw.endsWith('Z') || raw.includes('+') ? raw : raw + 'Z')
      const secs = Math.floor((Date.now() - started.getTime()) / 1000)
      elapsed = secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`
    }
    return {
      ...base,
      state: 'running',
      currentStep: live?.currentStep || null,
      elapsed,
      demandNotes: partials?.demand?.execution_summary?.notes || null,
      analystNotes: partials?.analyst?.execution_summary?.notes || null,
    }
  }

  // ── Workflow failed ───────────────────────────────────────────────────────
  if (statusStr === 'FAILED') {
    return {
      ...base,
      state: 'failed',
      currentStep: live?.currentStep || null,
      error: snapshot?.error || liveStatus?.error || null,
    }
  }

  // ── Workflow completed ────────────────────────────────────────────────────
  if (statusStr === 'COMPLETED' || isDone) {
    const designer = partials?.designer || {}
    const analyst = partials?.analyst || {}
    const demand = partials?.demand || {}

    // Extract metrics — priority: projectMeta > partials.metrics (flat) > designer.models[0]
    const metrics = {}
    const metaSrc = projectMeta?.last_model?.metrics || {}
    const flatSrc = partials?.metrics || {}
    const modelsSrc = (() => {
      const first = partials?.designer?.models?.[0]
      if (!first?.metrics) return {}
      const m = {}
      for (const mm of first.metrics) if (mm.metric_name) m[mm.metric_name] = mm.metric_value
      return m
    })()
    const rawMetrics = Object.keys(metaSrc).length ? metaSrc
      : Object.keys(flatSrc).length ? flatSrc
      : modelsSrc
    for (const [k, v] of Object.entries(rawMetrics)) {
      if (typeof v === 'number') metrics[k] = Number.isInteger(v) ? v : v.toFixed(4)
      else if (typeof v === 'string') metrics[k] = v
    }

    // Feature importance — from designer partials
    const fiRaw = designer?.feature_importance
      || designer?.execution_summary?.feature_importance
      || partials?.feature_importance
      || []
    const featureImportance = Array.isArray(fiRaw)
      ? fiRaw.map(f => ({
          name: typeof f === 'object' ? (f.feature || f.name || '') : String(f),
          value: typeof f === 'object' ? String(f.importance ?? f.value ?? '') : '',
        }))
      : []

    // Monitoring tab
    if (activeTab === 'monitoring') {
      const drift = monitoringData?.drift || {}
      const mon = monitoringData?.metrics || {}
      const isV2 = mon && mon.schema_version === 2

      // auc_drift > 0.6 = drift detected, > 0.8 = strong drift
      const auc = typeof drift.auc_drift === 'number'
        ? drift.auc_drift
        : (isV2 && typeof mon?.drift?.auc === 'number' ? mon.drift.auc : null)
      const driftDetected = auc !== null ? auc > 0.6 : null
      const driftStrength = auc === null ? null : auc > 0.8 ? 'strong' : auc > 0.6 ? 'moderate' : 'none'

      const fmtNum = (v) => (Number.isInteger(v) ? v : v.toFixed(4))
      const refMetrics = {}
      const prodMetrics = {}

      if (isV2) {
        // v2 schema: pull baseline from mon.reference, prod from real → expected.expected
        if (mon.reference) {
          for (const [k, v] of Object.entries(mon.reference)) {
            if (typeof v === 'number') refMetrics[k] = fmtNum(v)
          }
        }
        if (mon.real) {
          for (const [k, v] of Object.entries(mon.real)) {
            if (k === 'n_samples') continue
            if (typeof v === 'number') prodMetrics[k] = fmtNum(v)
          }
        }
        if (mon.expected) {
          for (const [k, v] of Object.entries(mon.expected)) {
            if (['n_samples', 'drift_level', 'drift_penalty', 'drift_auc'].includes(k)) continue
            if (v && typeof v === 'object' && typeof v.expected === 'number' && prodMetrics[k] == null) {
              prodMetrics[k] = fmtNum(v.expected)
            }
          }
        }
      } else {
        // legacy v1: flat dict with optional nested {expected, reference, ci}
        const skipKeys = new Set(['type', 'source', 'note', 'data_type', 'reference', 'drift_level', 'drift_penalty', 'drift_auc'])
        for (const [k, v] of Object.entries(mon)) {
          if (skipKeys.has(k)) continue
          if (typeof v === 'number') {
            prodMetrics[k] = fmtNum(v)
          } else if (v && typeof v === 'object' && !Array.isArray(v)) {
            if (typeof v.expected === 'number') prodMetrics[k] = fmtNum(v.expected)
            if (typeof v.reference === 'number') refMetrics[k] = fmtNum(v.reference)
          }
        }
        if (mon.reference && typeof mon.reference === 'object' && !Array.isArray(mon.reference)) {
          for (const [k, v] of Object.entries(mon.reference)) {
            if (typeof v === 'number' && !refMetrics[k]) refMetrics[k] = fmtNum(v)
          }
        }
      }

      const isTs = isV2 && mon?.task_type === 'time_series'
      return {
        ...base,
        state: 'monitoring',
        taskType: mon?.task_type || demand?.execution_summary?.task_type || null,
        targetColumn: demand?.execution_summary?.target_column || mon?.target_column || null,
        // Drift
        driftDetected,
        driftStrength,
        driftScore: auc !== null ? auc.toFixed(3) : null,
        driftSummary: drift.summary || null,
        topDriftFeatures: Array.isArray(drift.top_features)
          ? drift.top_features
          : (Array.isArray(mon?.drift?.top_features) ? mon.drift.top_features : []),
        // Metrics
        productionMetrics: Object.keys(prodMetrics).length ? prodMetrics : null,
        referenceMetrics: Object.keys(refMetrics).length ? refMetrics : null,
        nProductionSamples: mon.n_production_samples || mon.n_total || null,
        nLabeled: isV2 ? mon.n_labeled : null,
        nUnlabeled: isV2 ? mon.n_unlabeled : null,
        metricsType: mon.type || null,
        labeledMeetsThreshold: isV2 ? !!mon.labeled_meets_threshold : null,
        // Time-series specifics — null for everything else
        isTimeSeries: isTs,
        predictionLength: isTs ? mon?.prediction_length || null : null,
        frequency: isTs ? mon?.frequency || null : null,
        bestModel: isTs ? mon?.best_model || null : null,
        timeColumn: isTs ? mon?.time_column || null : null,
        idColumn: isTs ? mon?.id_column || null : null,
        conceptDriftLevel: isTs ? mon?.drift?.level || null : null,
        targetMeanShiftPct: isTs && typeof mon?.drift?.mean_shift_pct === 'number'
          ? mon.drift.mean_shift_pct : null,
        noMonitoringData: !monitoringData,
      }
    }

    // Inference tab
    if (activeTab === 'inference') {
      const inputCols = (tables || []).flatMap(t => (t.columns || []).map(c => c.name))
      return {
        ...base,
        state: 'inference',
        taskType: demand?.execution_summary?.task_type || null,
        targetColumn: demand?.execution_summary?.target_column || null,
        inputColumns: inputCols,
        metrics,
      }
    }

    // Monitoring summary — included in completed context so AI can answer from any tab
    let monitoringSummary = null
    if (monitoringData) {
      const drift = monitoringData?.drift || {}
      const mon = monitoringData?.metrics || {}
      const auc = typeof drift.auc_drift === 'number' ? drift.auc_drift : null
      const parts = []
      if (auc !== null) {
        const detected = auc > 0.6
        const strength = auc > 0.8 ? 'strong' : auc > 0.6 ? 'moderate' : 'none'
        parts.push(`drift: ${detected ? `DETECTED (${strength}, AUC=${auc.toFixed(3)})` : `not detected (AUC=${auc.toFixed(3)})`}`)
      }
      if (Array.isArray(drift.top_features) && drift.top_features.length) {
        parts.push(`top drift features: ${drift.top_features.slice(0, 3).join(', ')}`)
      }
      // Add key metric comparison: prefer v2 (real or expected against reference),
      // fall back to legacy v1 nested {expected, reference}
      const monIsV2 = mon && mon.schema_version === 2
      let summarized = false
      if (monIsV2) {
        for (const key of ['R2', 'RMSE', 'MAE']) {
          const ref = mon.reference?.[key]
          const real = mon.real?.[key]
          const exp = mon.expected?.[key]
          if (typeof real === 'number' && typeof ref === 'number') {
            parts.push(`${key} real=${real.toFixed(4)} vs reference=${ref.toFixed(4)}`)
            summarized = true
            break
          }
          if (exp && typeof exp.expected === 'number' && typeof ref === 'number') {
            parts.push(`${key} expected=${exp.expected.toFixed(4)} vs reference=${ref.toFixed(4)}`)
            summarized = true
            break
          }
        }
      }
      if (!summarized) {
        for (const key of ['R2', 'RMSE', 'MAE']) {
          const v = mon[key]
          if (v && typeof v === 'object' && typeof v.expected === 'number' && typeof v.reference === 'number') {
            parts.push(`${key} expected=${v.expected.toFixed(4)} vs reference=${v.reference.toFixed(4)}`)
            break
          }
        }
      }
      if (parts.length) monitoringSummary = parts.join('; ')
    }

    // Default: completed results view
    return {
      ...base,
      state: 'completed',
      taskType: demand?.execution_summary?.task_type || null,
      targetColumn: demand?.execution_summary?.target_column || null,
      modelType: designer?.execution_summary?.model_type || designer?.model_type || null,
      metrics,
      featureImportance,
      summary: demand?.execution_summary?.notes || analyst?.execution_summary?.notes || null,
      designerNotes: designer?.execution_summary?.notes || null,
      monitoringSummary,
    }
  }

  // ── No workflow / still loading ───────────────────────────────────────────
  return { ...base, state: 'no_workflow' }
}
